import { memo } from 'react'
import { Check, Plus, Undo2 } from 'lucide-react'

export type ItemState = 'new' | 'saved' | 'removed'

export interface SelectableItemProps {
  id: string
  name: string
  subtitle?: string
  badges?: React.ReactNode
  state?: ItemState
  isInOrGroup?: boolean
  groupPickLabel?: string // e.g. "2/4" for pick-2-of-4 groups, shown instead of "OR"
  onToggle: (id: string) => void
  children?: React.ReactNode
}

const getConfig = (state?: ItemState) => {
  if (state === 'removed') {
    return {
      container: 'bg-[var(--status-error-bg)]/30',
      text: 'line-through text-[var(--text-muted)]',
      button: {
        className: 'text-[var(--status-error-text)] border-[var(--status-error-border)] bg-[var(--status-error-bg)] hover:bg-[var(--status-error-bg)]',
        text: 'Undo',
        icon: <Undo2 className="w-3.5 h-3.5" />,
      },
    }
  }

  if (state === 'new' || state === 'saved') {
    return {
      container: 'bg-[var(--status-success-bg)]/50',
      text: 'text-[var(--text-primary)]',
      button: {
        className: 'text-[var(--status-success-text)] bg-[var(--status-success-bg)] border-[var(--status-success-border)]',
        text: 'Added',
        icon: <Check className="w-3.5 h-3.5" />,
      },
    }
  }

  return {
    container: 'hover:bg-[var(--surface-hover)]',
    text: 'text-[var(--text-primary)]',
    button: {
      className: 'text-[var(--text-secondary)] border-[var(--border-input)] bg-[var(--surface-card)] hover:bg-[var(--surface-hover)]',
      text: 'Add',
      icon: <Plus className="w-3.5 h-3.5" />,
    },
  }
}

function StateBadge({ state }: { state?: ItemState }) {
  if (state === 'new') {
    return (
      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[var(--status-info-bg)] text-[var(--status-info-text)]">
        New
      </span>
    )
  }
  return null
}

export const SelectableItem = memo(function SelectableItem({
  id,
  name,
  subtitle,
  badges,
  state,
  isInOrGroup,
  groupPickLabel,
  onToggle,
  children,
}: SelectableItemProps) {
  const config = getConfig(state)
  const isAdded = state === 'new' || state === 'saved'

  return (
    <div
      className={`group flex items-center gap-3 py-3 px-3 -mx-1 rounded-xl transition-all duration-150 cursor-pointer ${config.container}`}
      onClick={() => onToggle(id)}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className={`text-sm font-medium ${config.text}`}>
            {name}
          </span>
          {badges}
        </div>
        {subtitle && (
          <span className="block text-[11px] text-[var(--text-muted)] mt-0.5">
            {subtitle}
          </span>
        )}
      </div>

      {children}

      {/* OR/pick badge — shows group membership on added items */}
      {isInOrGroup && isAdded && (
        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-[var(--status-info-bg)] text-[var(--status-info-text)] tabular-nums">
          {groupPickLabel || 'OR'}
        </span>
      )}

      <StateBadge state={state} />

      <button
        type="button"
        aria-label={`${config.button.text} ${name}`}
        onClick={(e) => { e.stopPropagation(); onToggle(id) }}
        className={`flex items-center gap-1.5 shrink-0 h-8 px-3 rounded-lg border text-xs font-semibold transition-all duration-150 active:scale-[0.97] !cursor-pointer ${config.button.className}`}
      >
        {config.button.icon}
        <span>{config.button.text}</span>
      </button>
    </div>
  )
})
